import React, { useMemo } from 'react';

/**
 * ScoreDistributionBars — 4-bucket stacked histogram of trust scores.
 *
 * Buckets (mirrors V1 design):
 *   90-100  Excellent  (green)
 *   80-89   Good       (cyan)
 *   70-79   Fair       (amber)
 *   <70     At risk    (red)
 *
 * Pure presentational — caller computes the bucket counts and passes
 * them in. Renders one full-width stacked bar plus a row-per-bucket
 * legend with count + percentage.
 *
 * Phase A5 / G305 — used in DashboardTab.
 *
 * @param {Object} props
 * @param {number} props.excellent   — count of agents with trust ≥ 90
 * @param {number} props.good        — count with 80-89
 * @param {number} props.fair        — count with 70-79
 * @param {number} props.atRisk      — count with < 70
 * @param {boolean} [props.showLegend=true]
 */
export function ScoreDistributionBars({ excellent = 0, good = 0, fair = 0, atRisk = 0, showLegend = true }) {
  const total = excellent + good + fair + atRisk;
  const rows = useMemo(() => {
    if (total === 0) return [];
    return [
      { key: 'excellent', label: '90-100',  qualifier: 'Excellent', count: excellent, tone: 'success' },
      { key: 'good',      label: '80-89',   qualifier: 'Good',      count: good,      tone: 'cyan'    },
      { key: 'fair',      label: '70-79',   qualifier: 'Fair',      count: fair,      tone: 'warning' },
      { key: 'atRisk',    label: '<70',     qualifier: 'At risk',   count: atRisk,    tone: 'danger'  },
    ].map((r) => ({ ...r, pct: total > 0 ? Math.round((r.count / total) * 100) : 0 }));
  }, [excellent, good, fair, atRisk, total]);

  const TONE_BG = {
    success: 'bg-v2-success',
    cyan:    'bg-v2-accent-cyan',
    warning: 'bg-v2-warning',
    danger:  'bg-v2-danger',
  };
  const TONE_BADGE = {
    success: 'text-v2-success-strong bg-v2-success-soft',
    cyan:    'text-v2-accent-cyan-strong bg-v2-accent-cyan-soft',
    warning: 'text-v2-warning-strong bg-v2-warning-soft',
    danger:  'text-v2-danger-strong bg-v2-danger-soft',
  };

  if (total === 0) {
    return (
      <div className="text-[12px] text-v2-ink-muted italic py-2">
        No trust scores recorded.
      </div>
    );
  }

  return (
    <div>
      {/* Stacked bar — full width visualisation of the entire fleet. */}
      <div className="flex h-3 rounded-v2-pill overflow-hidden mb-3 bg-v2-surface-muted"
           role="img" aria-label={`Score distribution: ${rows.map((r) => `${r.qualifier} ${r.pct}%`).join(', ')}`}>
        {rows.map((r) => r.pct > 0 && (
          <div key={r.key} className={TONE_BG[r.tone]} style={{ width: `${r.pct}%` }} title={`${r.qualifier}: ${r.count} agent${r.count === 1 ? '' : 's'} (${r.pct}%)`} />
        ))}
      </div>

      {showLegend && (
        <div className="space-y-1.5">
          {rows.map((r) => (
            <div key={r.key} className="flex items-center gap-2 text-[12px]">
              <span className={`text-[10.5px] font-semibold rounded-v2-pill px-2 py-0.5 ${TONE_BADGE[r.tone]}`}>
                {r.label}
              </span>
              <span className="text-v2-ink-secondary">{r.qualifier}</span>
              <span className="ml-auto font-mono-v2 text-v2-ink">
                {r.count} <span className="text-v2-ink-muted">· {r.pct}%</span>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default ScoreDistributionBars;
